import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Unlock, Lock, X, Check, Clock, Filter } from 'lucide-react';
import { getUnlockRequests, approveUnlockRequest, denyUnlockRequest } from '../utils/api';
import { formatDateDisplay } from '../utils/weekUtils';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const AdminUnlockRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, all, approved, denied
  const [denyModal, setDenyModal] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  const token = localStorage.getItem('agentToken');

  useEffect(() => {
    if (!token) { navigate('/agent/login'); return; }
    loadRequests();
  }, [navigate]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const res = await getUnlockRequests(token);
      setRequests(res.requests || []);
    } catch (e) {
      toast.error('Failed to load unlock requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (reqId) => {
    try {
      await approveUnlockRequest(reqId, token);
      toast.success('Goals unlocked for agent');
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDeny = async () => {
    if (!denyModal) return;
    try {
      await denyUnlockRequest(denyModal, denyReason, token);
      toast.success('Request denied');
      setDenyModal(null);
      setDenyReason('');
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deny');
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const statusBadge = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'denied': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <AgentLayout title="Unlock Requests">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Unlock Requests">
      <div data-testid="admin-unlock-requests-page">

        {/* Header + Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4" data-testid="unlock-requests-header">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Goal Unlock Requests</h2>
              <p className="text-sm text-gray-500">
                {pendingCount > 0 ? `${pendingCount} pending request${pendingCount > 1 ? 's' : ''}` : 'No pending requests'}
              </p>
            </div>
            <div className="flex gap-2" data-testid="request-filters">
              {['pending', 'approved', 'denied', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition capitalize ${
                    filter === f ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${f}`}
                >
                  <Filter size={12} /> {f}
                  {f === 'pending' && pendingCount > 0 && (
                    <span className="ml-1 bg-white text-brand-blue rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">{pendingCount}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-400" data-testid="no-requests">
              <Unlock size={40} className="mx-auto mb-3" />
              <p>{filter === 'pending' ? 'No pending unlock requests.' : 'No requests found.'}</p>
            </div>
          )}

          {filtered.map(req => (
            <div key={req.id} className="bg-white rounded-lg shadow-md p-4" data-testid={`unlock-request-${req.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h4 className="font-bold text-gray-900">{req.agentName}</h4>
                    <span className="text-xs text-gray-400">{req.agentEmail}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${statusBadge(req.status)}`}>
                      {req.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Lock size={12} /> Week: <strong>{formatDateDisplay(req.weekStartDate)}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {req.reason && (
                    <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded">
                      <strong>Reason:</strong> {req.reason}
                    </p>
                  )}
                  {req.denyReason && (
                    <p className="text-sm text-red-500 mt-2 bg-red-50 p-2 rounded">
                      <strong>Denied reason:</strong> {req.denyReason}
                    </p>
                  )}
                </div>

                {req.status === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(req.id)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition font-semibold text-sm min-h-[44px]"
                      data-testid={`approve-${req.id}`}
                    >
                      <Check size={14} /> Approve & Unlock
                    </button>
                    <button
                      onClick={() => setDenyModal(req.id)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition font-semibold text-sm min-h-[44px]"
                      data-testid={`deny-${req.id}`}
                    >
                      <X size={14} /> Deny
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Deny Modal */}
        {denyModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="deny-modal">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Deny Unlock Request</h3>
              <p className="text-sm text-gray-500 mb-4">Optionally provide a reason for denying this request.</p>
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
                placeholder="Reason for denial (optional)..."
                data-testid="deny-reason-input"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setDenyModal(null); setDenyReason(''); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition font-semibold text-sm">
                  Cancel
                </button>
                <button onClick={handleDeny} className="flex items-center gap-1.5 px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition font-semibold text-sm">
                  <X size={14} /> Deny Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminUnlockRequests;
